import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const authFormSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

type AuthFormValues = z.infer<typeof authFormSchema>;

const SocialButton = ({ provider, onClick, children, ...props }: { provider: string; onClick: () => void; children: React.ReactNode; [key: string]: any }) => {
  const providerStyles: Record<string, string> = {
    Google: 'border-[#4285F4] text-[#4285F4] hover:bg-[#e8f0fe] focus:ring-2 focus:ring-[#4285F4]',
    Facebook: 'border-blue-600 text-blue-700 hover:bg-blue-50',
    Apple: 'border-black text-black hover:bg-gray-100',
  };
  const icons: Record<string, React.ReactNode> = {
    Google: (
      <svg width="20" height="20" viewBox="0 0 48 48" fill="none"><g><path d="M44.5 20H24V28.5H36.9C35.2 33.1 30.9 36 25.5 36C18.6 36 13 30.4 13 23.5C13 16.6 18.6 11 25.5 11C28.6 11 31.3 12.1 33.3 13.9L39.1 8.1C35.5 4.8 30.8 3 25.5 3C13.7 3 4 12.7 4 24.5C4 36.3 13.7 46 25.5 46C36.1 46 45 37.1 45 26.5C45 24.9 44.8 22.4 44.5 20Z" fill="#FFC107"/><path d="M6.3 14.7L13.5 20.1C15.5 15.7 20.1 13 25.5 13C28.6 13 31.3 14.1 33.3 15.9L39.1 10.1C35.5 6.8 30.8 5 25.5 5C18.6 5 13 10.6 13 17.5C13 19.1 13.2 20.6 13.5 22.1L6.3 14.7Z" fill="#FF3D00"/><path d="M25.5 46C30.9 46 35.2 43.1 36.9 38.5L29.7 33.1C28.2 34.1 26.4 34.7 24.5 34.7C19.1 34.7 14.7 31.1 13.5 26.5L6.3 33.9C9.2 40.1 16.1 46 25.5 46Z" fill="#4CAF50"/><path d="M44.5 20H24V28.5H36.9C36.2 30.6 34.7 32.3 32.7 33.1L39.1 38.5C42.2 35.7 44.5 31.6 44.5 27.5C44.5 25.9 44.3 23.4 44.5 20Z" fill="#1976D2"/></g></svg>
    ),
    Facebook: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/></svg>
    ),
    Apple: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16.365 1.43c0 1.14-.93 2.07-2.07 2.07-.02 0-.04 0-.06-.01-.02-.01-.03-.01-.05-.01-.02 0-.04 0-.06.01-.02.01-.04.01-.06.01-1.14 0-2.07-.93-2.07-2.07 0-1.14.93-2.07 2.07-2.07.02 0 .04 0 .06.01.02.01.03.01.05.01.02 0 .04 0 .06-.01.02-.01.04-.01.06-.01 1.14 0 2.07.93 2.07 2.07zm4.13 13.13c-.01-2.13 1.74-3.15 1.82-3.2-1-1.46-2.56-1.66-3.11-1.68-1.32-.13-2.58.77-3.25.77-.67 0-1.7-.75-2.8-.73-1.44.02-2.77.84-3.51 2.13-1.5 2.6-.38 6.45 1.08 8.56.71 1.03 1.56 2.19 2.68 2.15 1.08-.04 1.49-.7 2.8-.7 1.31 0 1.67.7 2.8.68 1.16-.02 1.89-1.05 2.6-2.08.82-1.19 1.16-2.34 1.18-2.4-.03-.01-2.27-.87-2.29-3.45zm-3.7-12.13c.01 0 .01 0 0 0z"/></svg>
    ),
  };
  return (
    <Button
      type="button"
      variant="outline"
      className={`w-full flex items-center justify-center gap-2 mb-0 ${providerStyles[provider]}`}
      onClick={onClick}
      {...props}
    >
      {icons[provider]} {children}
    </Button>
  );
};

const AuthPage = () => {
  const { signIn, signUp, user, loading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(authFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: AuthFormValues) => {
    setIsSubmitting(true);
    try {
      if (activeTab === 'login') {
        await signIn(data.email, data.password);
      } else {
        await signUp(data.email, data.password);
        setActiveTab('login');
        form.reset();
        setAcceptTerms(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // If user is already logged in, redirect to home
  if (user && !loading) {
    return <Navigate to="/" />;
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-2">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl text-center font-bold text-cricket-green">Cricket Scoring System</CardTitle>
          <CardDescription className="text-center">
            {activeTab === 'login' 
              ? "Sign in to your account" 
              : "Create a new account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>
            <div className="flex items-center my-4">
              <Separator className="flex-1" />
              <span className="mx-2 text-xs text-muted-foreground">Continue with email</span>
              <Separator className="flex-1" />
            </div>
            <TabsContent value="login">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="you@example.com" {...field} autoComplete="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} autoComplete="current-password" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-cricket-green hover:bg-cricket-green/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>
              </Form>
              <div className="flex items-center my-6">
                <Separator className="flex-1" />
                <span className="mx-2 text-xs text-muted-foreground">or sign in with</span>
                <Separator className="flex-1" />
              </div>
              <div className="flex flex-col gap-2">
                <SocialButton provider="Google" onClick={() => { /* TODO: Add Google login logic */ }} aria-label="Sign in with Google">
                  Continue with Google
                </SocialButton>
                <SocialButton provider="Facebook" onClick={() => { /* TODO: Add Facebook login logic */ }} aria-label="Sign in with Facebook">
                  Continue with Facebook
                </SocialButton>
                <SocialButton provider="Apple" onClick={() => { /* TODO: Add Apple login logic */ }} aria-label="Sign in with Apple">
                  Continue with Apple
                </SocialButton>
              </div>
            </TabsContent>
            <TabsContent value="register">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="you@example.com" {...field} autoComplete="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} autoComplete="new-password" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center space-x-2">
                    <Checkbox id="terms" checked={acceptTerms} onCheckedChange={checked => setAcceptTerms(!!checked)} />
                    <label htmlFor="terms" className="text-xs text-muted-foreground cursor-pointer">
                      I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-cricket-green hover:text-cricket-green/80">Terms and Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-cricket-green hover:text-cricket-green/80">Privacy Policy</a>.
                    </label>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-cricket-green hover:bg-cricket-green/90"
                    disabled={isSubmitting || !acceptTerms}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>
              </Form>
              <div className="flex items-center my-6">
                <Separator className="flex-1" />
                <span className="mx-2 text-xs text-muted-foreground">or sign up with</span>
                <Separator className="flex-1" />
              </div>
              <div className="flex flex-col gap-2">
                <SocialButton provider="Google" onClick={() => { /* TODO: Add Google login logic */ }} aria-label="Sign up with Google">
                  Continue with Google
                </SocialButton>
                <SocialButton provider="Facebook" onClick={() => { /* TODO: Add Facebook login logic */ }} aria-label="Sign up with Facebook">
                  Continue with Facebook
                </SocialButton>
                <SocialButton provider="Apple" onClick={() => { /* TODO: Add Apple login logic */ }} aria-label="Sign up with Apple">
                  Continue with Apple
                </SocialButton>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="flex flex-col items-center gap-2">
          <p className="text-sm text-gray-500">
            {activeTab === 'login' 
              ? (
                <>Don't have an account? Click <button type="button" className="underline text-cricket-green hover:text-cricket-green/80 font-medium" onClick={() => setActiveTab('register')}>Register</button> above.</>
              )
              : (
                <>Already have an account? Click <button type="button" className="underline text-cricket-green hover:text-cricket-green/80 font-medium" onClick={() => setActiveTab('login')}>Login</button> above.</>
              )}
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AuthPage;
